import {
  HStack,
  IconButton,
  Input,
  Tooltip,
  useToast,
  NumberInput,
  NumberInputField,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Badge,
  useColorModeValue,
  Text,
  Spacer,
  VStack,
  Portal,
} from '@chakra-ui/react';
import { useMutation } from 'urql';
import { faPen, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDragData } from '../../utils/property-dragging-utils';
import {
  BulkDeleteSparkelPropertiesDocument,
  BulkUpsertSparkelPropertiesDocument,
  EventResource,
  EventType,
  ElementCategoryEnum,
  TagColorScheme,
} from '../../gql/graphql';
import { EventName, useMixpanel } from '../../services/mixpanel';
import {
  ElementCategory,
  getAttributeTypeIcon,
} from '../company/ElementCategories';
import { DbIdsPerModel } from './ForgeViewer';
import { useBreadcrumb } from './BreadcrumbProvider';
import SparkelIcon from './icon/SparkelIcon';
import StyledKeyValuesProperties from './StyledKeyValuesProperties';
import { useEventHistory } from 'src/hooks/event-history';
import { UnitType } from 'src/utils/unit-utils';
import { useProjectHistory } from 'src/components/project/project-history/hooks/useProjectHistory';
import { useDiffing } from 'src/components/project/project-history/hooks/useDiffing';
import { DiffText } from 'src/components/project/project-history/DiffText';
import { DiffBadge } from 'src/components/project/project-history/DiffBadge';

interface ElementAttributeItemProps {
  category: string;
  values: (string | number)[];
  selectedDbIds: DbIdsPerModel;
  projectId: string;
  categoryData: ElementCategory;
}

export default memo(function ElementAttributeItem({
  category,
  values,
  selectedDbIds,
  projectId,
  categoryData,
}: ElementAttributeItemProps): React.ReactElement {
  const [inputValue, setInputValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [shouldAutofocus, setShouldAutofocus] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [optimisticValue, setOptimisticValue] = useState<
    (string | number)[] | null
  >(null);

  const [, bulkCreateSparkelProps] = useMutation(
    BulkUpsertSparkelPropertiesDocument
  );
  const [, bulkDeleteSparkelProps] = useMutation(
    BulkDeleteSparkelPropertiesDocument
  );

  const { trackEvent } = useMixpanel();
  const { logEvent } = useEventHistory(projectId);
  const { projectName } = useBreadcrumb();
  const toast = useToast();

  const { t } = useTranslation('project');

  // History mode support
  const { isHistoryDrawerOpen, referenceState, diff } = useProjectHistory();
  const {
    getBackgroundColor,
    getEventType,
    getHistoricalAttributes,
    getHistoricalAttributeValue,
  } = useDiffing();

  // Extract single selection urn and dbId
  const singleSelection = useMemo(() => {
    const entries = Object.entries(selectedDbIds);
    if (entries.length !== 1) return undefined;
    const [urn, idList] = entries[0];
    if (!Array.isArray(idList) || idList.length !== 1) return undefined;
    return { urn, dbId: idList[0] } as { urn: string; dbId: number };
  }, [selectedDbIds]);

  const attributeDiff = useMemo(() => {
    if (!isHistoryDrawerOpen || !singleSelection || !diff) {
      return undefined;
    }

    const { urn, dbId } = singleSelection;
    return getHistoricalAttributes(referenceState, diff, urn, dbId, category);
  }, [
    isHistoryDrawerOpen,
    referenceState,
    diff,
    singleSelection,
    category,
    getHistoricalAttributes,
  ]);

  // Determine what values to display - prioritize historical values when in history mode
  const displayValues = useMemo(() => {
    // Use optimistic value if set (for immediate UI feedback)
    if (optimisticValue !== null) {
      return optimisticValue;
    }

    if (!isHistoryDrawerOpen || !singleSelection || !referenceState) {
      // Not in history mode, use current database values
      return values;
    }

    const { urn, dbId } = singleSelection;

    // If there's a diff for this attribute, use the diff values
    if (attributeDiff) {
      // For diffs, we want to show the historical value (old) when available
      if (attributeDiff.old !== undefined) {
        return [attributeDiff.old];
      }
      // If no old value, this means the attribute was created in this diff
      // Don't show current values, show nothing (will be handled by placeholder)
      return [];
    }

    // No diff, but we're in history mode - get the historical value from referenceState
    const historicalValue = getHistoricalAttributeValue(
      referenceState,
      urn,
      dbId,
      category
    );
    if (historicalValue !== undefined) {
      return [historicalValue];
    }

    // No historical value found in referenceState - don't show current values
    // This means the attribute didn't exist at this historical point
    return [];
  }, [
    isHistoryDrawerOpen,
    singleSelection,
    referenceState,
    values,
    attributeDiff,
    category,
    getHistoricalAttributeValue,
    optimisticValue,
  ]);

  const uniqueValuesString = useMemo(() => {
    return [...new Set(displayValues)]
      .map((value) => value.toString())
      .join(', ');
  }, [displayValues]);

  const renderValues = useMemo(() => {
    const uniqueValues = [...new Set(displayValues)].map((value) =>
      value.toString()
    );

    // If no values to display, show "No value" placeholder
    if (uniqueValues.length === 0) {
      return (
        <Text fontSize="xs" color="gray.500" fontStyle="italic">
          {t('attributes.no-value')}
        </Text>
      );
    }

    if (categoryData.type === ElementCategoryEnum.Dropdown) {
      return (
        <HStack spacing={1} wrap="wrap">
          {uniqueValues.map((value, index) => (
            <Badge
              key={index}
              colorScheme={
                categoryData.dropdownValues
                  .find((option) => option.value === value)
                  ?.colorScheme?.toLowerCase() || 'blue'
              }
              variant="subtle"
              size="sm"
              borderRadius="md"
              textTransform="none"
            >
              {value}
            </Badge>
          ))}
        </HStack>
      );
    } else if (categoryData.type === ElementCategoryEnum.Boolean) {
      return (
        <HStack spacing={1} wrap="wrap">
          {uniqueValues.map((value, index) => (
            <Badge
              key={index}
              colorScheme={value === 'Yes' ? 'blue' : 'purple'}
              variant="subtle"
              size="sm"
              borderRadius="md"
            >
              {value}
            </Badge>
          ))}
        </HStack>
      );
    } else if (categoryData.type === ElementCategoryEnum.Length) {
      return (
        <HStack spacing={1} marginTop={1} align="center">
          <Text fontSize="xs">{uniqueValuesString}</Text>
          <Text fontSize="xs" color="gray.500" fontWeight="medium">
            m
          </Text>
        </HStack>
      );
    } else if (categoryData.type === ElementCategoryEnum.Area) {
      return (
        <HStack spacing={1} marginTop={1} align="center">
          <Text fontSize="xs">{uniqueValuesString}</Text>
          <Text fontSize="xs" color="gray.500" fontWeight="medium">
            m²
          </Text>
        </HStack>
      );
    } else if (categoryData.type === ElementCategoryEnum.Volume) {
      return (
        <HStack spacing={1} marginTop={1} align="center">
          <Text fontSize="xs">{uniqueValuesString}</Text>
          <Text fontSize="xs" color="gray.500" fontWeight="medium">
            m³
          </Text>
        </HStack>
      );
    }

    return <>{uniqueValuesString}</>;
  }, [
    categoryData.type,
    uniqueValuesString,
    categoryData.dropdownValues,
    displayValues,
    t,
  ]);

  useEffect(() => {
    // Reset optimistic value when actual values change (mutation completed)
    // Only reset if we had an optimistic value set
    setOptimisticValue((prev) => {
      // Reset if we had an optimistic value and values prop has updated
      // This happens when the GraphQL cache updates after mutation completes
      return prev !== null ? null : prev;
    });
    // Don't reset inputValue when loading to preserve the optimistic value in the input
    if (!isLoading) {
      setInputValue(uniqueValuesString || '');
    }
    // Disable editing in history mode; otherwise allow empty-value auto edit
    // Don't reset editing mode if we're currently loading (to keep input visible during mutations)
    if (!isLoading) {
      setIsEditing(isHistoryDrawerOpen ? false : values.length === 0);
    }
    setShouldAutofocus(false);
  }, [values, uniqueValuesString, isHistoryDrawerOpen, isLoading]);

  const logAttributeUpdate = useCallback(
    (label: string, oldName: string) => {
      const affectedResources = Object.entries(selectedDbIds).flatMap(
        ([modelUrn, dbIds]) => dbIds.map((dbId) => `${modelUrn}:${dbId}`)
      );
      logEvent({
        eventName: 'Attribute Updated',
        eventType: EventType.Modified,
        resourceName: category,
        resourceType: EventResource.SparkelProperty,
        metadata: {
          affectedResources,
          details: `Assigned ${label} to ${category} for ${affectedResources.length} elements`,
          oldName,
          newName: label,
        },
      });
    },
    [category, logEvent, selectedDbIds]
  );

  const setOptimisticUpdate = useCallback(
    (label: string) => {
      const isNumericType =
        categoryData.type === ElementCategoryEnum.Number ||
        categoryData.type === ElementCategoryEnum.Length ||
        categoryData.type === ElementCategoryEnum.Area ||
        categoryData.type === ElementCategoryEnum.Volume;
      const parsedValue = isNumericType ? parseFloat(label) : label;
      setOptimisticValue(
        isNumericType && !isNaN(parsedValue as number) ? [parsedValue] : [label]
      );
      setInputValue(label);
    },
    [categoryData.type]
  );

  const handleDelete = useCallback(async () => {
    setIsLoading(true);
    // Optimistically clear the value immediately
    setOptimisticValue([]);
    setInputValue('');
    try {
      const result = await bulkDeleteSparkelProps({
        input: {
          projectId,
          dbIds: Object.entries(selectedDbIds).map(([modelUrn, dbIds]) => ({
            modelUrn,
            dbIds,
          })),
          thePropertySet: category,
        },
      });

      if (result.error) {
        // Reset optimistic value on error
        setOptimisticValue(null);
        toast({
          title: t('attributes.error.delete'),
          status: 'error',
        });
      } else {
        // Set affected resources to the urn-dbId key of the selectedDbIds
        const affectedResources = Object.entries(selectedDbIds).flatMap(
          ([modelUrn, dbIds]) => dbIds.map((dbId) => `${modelUrn}:${dbId}`)
        );

        logEvent({
          eventName: 'Attribute Deleted',
          eventType: EventType.Deleted,
          resourceName: category,
          resourceType: EventResource.SparkelProperty,
          metadata: {
            affectedResources,
            details: `Attributes removed from ${affectedResources.length} elements`,
          },
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    bulkDeleteSparkelProps,
    category,
    logEvent,
    projectId,
    selectedDbIds,
    t,
    toast,
  ]);

  const handleSubmit = useCallback(
    async (label: string) => {
      setIsLoading(true);
      try {
        if (label === '') {
          await handleDelete();
          return;
        }

        trackEvent(EventName.SparkelAttributeAssigned, {
          'Sparkel Attribute Name': category,
          'Sparkel Attribute Value': label,
          'Project Name': projectName,
          'Project ID': projectId,
        });

        logEvent({
          projectId,
          eventName: `Attribute ${category} changed to ${label} for ${
            Object.entries(selectedDbIds).length
          } elements`,
          eventType: EventType.Modified,
          resourceName: 'Sparkel Attribute',
          resourceType: EventResource.SparkelProperty,
          resolved: false,
        });

        const result = await bulkCreateSparkelProps({
          input: {
            projectId,
            dbIds: Object.entries(selectedDbIds).map(([modelUrn, dbIds]) => ({
              modelUrn,
              dbIds,
            })),
            thePropertySet: category,
            thePropertyValue: label,
          },
        });

        if (result.error) {
          setOptimisticValue(null);
          setIsEditing(false); // Switch back to display mode on error
          toast({
            title: t('attributes.error.assign'),
            status: 'error',
          });
          return;
        }

        logAttributeUpdate(label, uniqueValuesString);

        // Switch to display mode after successful mutation
        setIsEditing(false);
      } catch (e) {
        setOptimisticValue(null);
        setIsEditing(false); // Switch back to display mode on error
        toast({
          title: t('attributes.error.assign'),
          status: 'error',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [
      trackEvent,
      category,
      projectName,
      projectId,
      bulkCreateSparkelProps,
      selectedDbIds,
      handleDelete,
      toast,
      t,
      logEvent,
      uniqueValuesString,
      logAttributeUpdate,
    ]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    },
    []
  );

  const handleNumberChange = useCallback((valueAsString: string) => {
    setInputValue(valueAsString);
  }, []);

  const handleMenuItemClick = useCallback(
    (value: string) => {
      setInputValue(value);
      handleSubmit(value);
    },
    [handleSubmit]
  );

  const handleInputBlur = useCallback(() => {
    setShouldAutofocus(false);
    if (inputValue !== uniqueValuesString) {
      // Set loading state immediately to prevent useEffect from resetting inputValue
      setIsLoading(true);
      // Set optimistic value immediately for both deletion and updates
      if (inputValue === '') {
        setOptimisticValue([]);
        setInputValue('');
        setIsEditing(true); // Keep editing mode for deletion
      } else {
        // For non-empty values, set optimistic update and keep editing mode
        // until mutation completes, then switch to display mode
        setOptimisticUpdate(inputValue);
        setIsEditing(true); // Keep editing mode to show loading state
      }
      handleSubmit(inputValue);
    } else {
      setIsEditing(false);
    }
  }, [handleSubmit, inputValue, uniqueValuesString, setOptimisticUpdate]);

  const handleEditClick = useCallback(() => {
    setIsEditing(true);
    setInputValue(uniqueValuesString || '');
    setShouldAutofocus(true);
  }, [uniqueValuesString]);

  const handleGetDragData = useCallback((key: string, originalKey?: string) => {
    return formatDragData({
      category: 'Sparkel Attributes',
      propertyName: originalKey || '',
      displayName: key,
    });
  }, []);

  const inputBackgroundColor = useColorModeValue('gray.50', 'gray.700');
  const inputBorderColor = useColorModeValue('gray.200', 'gray.700');
  const inputPlaceholderColor = useColorModeValue('gray.400', 'gray.500');

  const renderNumberInput = (unitType?: UnitType) => {
    let unit: string | null = null;
    if (unitType === UnitType.Length) {
      unit = 'm';
    } else if (unitType === UnitType.Area) {
      unit = 'm²';
    } else if (unitType === UnitType.Volume) {
      unit = 'm³';
    }

    return (
      <HStack spacing={2} marginTop={1} align="center">
        <NumberInput
          value={inputValue}
          onChange={handleNumberChange}
          onBlur={handleInputBlur}
          size="xs"
          variant="filled"
          focusBorderColor="cyan.500"
          flex={1}
        >
          <NumberInputField
            autoFocus={shouldAutofocus}
            disabled={isLoading}
            _focus={{
              borderWidth: '1px',
            }}
            _placeholder={{
              color: inputPlaceholderColor,
            }}
            backgroundColor={inputBackgroundColor}
            borderWidth="1px"
            borderColor={inputBorderColor}
            borderRadius="md"
            placeholder={uniqueValuesString || t('attributes.placeholder')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleInputBlur();
              }
            }}
          />
        </NumberInput>
        {unit && (
          <Text
            fontSize="xs"
            color="gray.500"
            fontWeight="medium"
            minWidth="fit-content"
            userSelect="none"
          >
            {unit}
          </Text>
        )}
      </HStack>
    );
  };

  const renderBooleanSelect = () => {
    const booleanValues = ['Yes', 'No'];
    return (
      <Menu>
        <MenuButton
          as={Button}
          size="xs"
          colorScheme="gray"
          borderRadius="md"
          marginTop={1}
          width="100%"
          textAlign="left"
          fontWeight="normal"
          color={uniqueValuesString ? 'inherit' : 'gray.500'}
          isLoading={isLoading}
          rightIcon={
            <SparkelIcon icon={faChevronDown} size="xs" color="inherit" />
          }
        >
          {inputValue || uniqueValuesString || t('attributes.placeholder')}
        </MenuButton>
        <MenuList>
          <MenuItem
            key="no-value"
            onClick={() => handleMenuItemClick('')}
            color="gray.500"
            fontStyle="italic"
          >
            No value
          </MenuItem>
          {booleanValues.map((value) => (
            <MenuItem key={value} onClick={() => handleMenuItemClick(value)}>
              <Badge
                colorScheme={value === 'Yes' ? 'blue' : 'purple'}
                borderRadius="md"
                size="sm"
              >
                {value}
              </Badge>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    );
  };

  const renderDropdownMenu = () => {
    const dropdownValues = categoryData.dropdownValues || [];
    return (
      <Menu>
        <MenuButton
          as={Button}
          size="xs"
          colorScheme="gray"
          borderRadius="md"
          marginTop={1}
          width="100%"
          textAlign="left"
          fontWeight="normal"
          color={uniqueValuesString ? 'inherit' : 'gray.500'}
          isLoading={isLoading}
          rightIcon={
            <SparkelIcon icon={faChevronDown} size="xs" color="inherit" />
          }
        >
          {inputValue || uniqueValuesString || t('attributes.placeholder')}
        </MenuButton>
        <Portal>
          <MenuList zIndex={1000}>
            <MenuItem
              key="no-value"
              onClick={() => handleMenuItemClick('')}
              color="gray.500"
              fontStyle="italic"
            >
              No value
            </MenuItem>
            {dropdownValues.map((option) => (
              <MenuItem
                key={option.value}
                onClick={() => handleMenuItemClick(option.value || '')}
              >
                <Badge
                  colorScheme={
                    (option.colorScheme as TagColorScheme).toLowerCase() ||
                    'blue'
                  }
                  textTransform="none"
                  borderRadius="md"
                  size="sm"
                >
                  {option.value}
                </Badge>
              </MenuItem>
            ))}
          </MenuList>
        </Portal>
      </Menu>
    );
  };

  const renderTextInput = () => (
    <Input
      id={'sparkel-attribute-input-' + category}
      autoFocus={shouldAutofocus}
      value={inputValue}
      onChange={handleInputChange}
      onBlur={handleInputBlur}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleInputBlur();
        }
      }}
      placeholder={uniqueValuesString || t('attributes.placeholder')}
      borderRadius={'md'}
      variant={'filled'}
      size={'xs'}
      marginTop={1}
      isDisabled={isLoading}
    />
  );

  const renderInputField = () => {
    switch (categoryData.type) {
      case ElementCategoryEnum.Number:
        return renderNumberInput();
      case ElementCategoryEnum.Length:
        return renderNumberInput(UnitType.Length);
      case ElementCategoryEnum.Area:
        return renderNumberInput(UnitType.Area);
      case ElementCategoryEnum.Volume:
        return renderNumberInput(UnitType.Volume);

      case ElementCategoryEnum.Boolean:
        return renderBooleanSelect();

      case ElementCategoryEnum.Dropdown:
        return renderDropdownMenu();

      default:
        return renderTextInput();
    }
  };

  const eventType = attributeDiff ? getEventType(attributeDiff) : undefined;

  const renderDefaultValue = useCallback(() => {
    if (displayValues.length === 0) {
      return (
        <Text fontSize="xs" color="gray.500" fontStyle="italic">
          {t('attributes.no-value')}
        </Text>
      );
    }
    return (
      <>
        {renderValues}
        {!isHistoryDrawerOpen && (
          <Tooltip label={t('attributes.edit-tooltip')}>
            <IconButton
              aria-label="Edit value"
              size={'xs'}
              colorScheme="gray"
              variant="ghost"
              color={'gray.400'}
              icon={<SparkelIcon icon={faPen} color={'inherit'} />}
              onClick={handleEditClick}
              opacity={isHovering ? 1 : 0}
            />
          </Tooltip>
        )}
      </>
    );
  }, [
    displayValues,
    renderValues,
    t,
    isHistoryDrawerOpen,
    isHovering,
    handleEditClick,
  ]);

  const renderAttributeValue = useCallback(() => {
    // When diff exists for this category and single selection, render diff UI
    if (attributeDiff) {
      const diffEventType = getEventType(attributeDiff);
      if (diffEventType) {
        return (
          <HStack
            fontWeight={'bold'}
            spacing={2}
            align="center"
            paddingX={1}
            borderRadius={'md'}
          >
            <DiffText
              oldValue={attributeDiff.old}
              newValue={attributeDiff.new}
            />
          </HStack>
        );
      }
    }
    // When editing or loading (to prevent showing old value during deletion), don't show display value
    if ((isEditing || isLoading) && !isHistoryDrawerOpen) return <></>;
    if (displayValues.length === 0 && !isHistoryDrawerOpen) return <></>;

    // In history mode or when there are values, always show something
    return <HStack fontWeight={'bold'}>{renderDefaultValue()}</HStack>;
  }, [
    attributeDiff,
    isEditing,
    isLoading,
    isHistoryDrawerOpen,
    displayValues,
    getEventType,
    renderDefaultValue,
  ]);

  return (
    <HStack
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      paddingX={2}
      marginTop={1}
      width={'100%'}
      backgroundColor={eventType ? getBackgroundColor(eventType) : undefined}
      borderRadius={'md'}
    >
      <VStack align="left" width={'100%'} gap={0}>
        <StyledKeyValuesProperties
          keyValues={[
            {
              key: category,
              originalKey: category,
              keyIcon: getAttributeTypeIcon(categoryData.type),
              value: renderAttributeValue(),
            },
          ]}
          getDragData={handleGetDragData}
        />
        {!isHistoryDrawerOpen &&
          (isEditing || displayValues.length === 0) &&
          renderInputField()}
      </VStack>
      {isHistoryDrawerOpen && eventType && (
        <>
          <Spacer />
          <DiffBadge eventType={eventType} />
        </>
      )}
    </HStack>
  );
});
